import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Image as ImageIcon, Search, X } from "lucide-react";
import { CachedImage } from "../../components/ui/CachedImage.jsx";
import { IconButton, MediaTile, PaginationButton } from "../../components/ui/Primitives.jsx";
import { formatTimestamp } from "../../lib/format.js";
import { getDisplayImageSrc, getThumbnailImageSrc } from "../../lib/imageAssets.js";

export function GalleryWorkspace({ history, onOpen }) {
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const searchInputRef = useRef(null);
  const pageSize = 18;
  const items = useMemo(() => [...history].filter((item) => getDisplayImageSrc(item)).reverse(), [history]);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredItems = useMemo(() => (
    normalizedQuery
      ? items.filter((item) => [
        item.prompt,
        item.model,
        item.aspectRatio,
        formatTimestamp(item.createdAt),
      ].filter(Boolean).join(" ").toLowerCase().includes(normalizedQuery))
      : items
  ), [items, normalizedQuery]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = filteredItems.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(filteredItems.length, currentPage * pageSize);
  const pageItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageControls = buildGalleryPagination(currentPage, pageCount);
  const countLabel = filteredItems.length === items.length
    ? `${items.length} 张作品`
    : `${filteredItems.length} / ${items.length} 张作品`;
  const searchExpanded = searchOpen || query.length > 0;

  useEffect(() => {
    setPage(1);
  }, [normalizedQuery]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  function openSearch() {
    setSearchOpen(true);
    window.requestAnimationFrame?.(() => searchInputRef.current?.focus());
  }

  function clearSearch() {
    setQuery("");
    setSearchOpen(false);
  }

  function handleSearchBlur(event) {
    if (query || event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setSearchOpen(false);
  }

  return (
    <section className={pageItems.length === 0 ? "gallery-page is-empty" : "gallery-page"} aria-labelledby="gallery-title">
      <div className="gallery-page-head">
        <div className="gallery-title-block">
          <h2 id="gallery-title">我的作品</h2>
          <span aria-live="polite">{countLabel}</span>
        </div>
        <form
          className={searchExpanded ? "gallery-search is-open" : "gallery-search"}
          role="search"
          onSubmit={(event) => event.preventDefault()}
          onBlur={handleSearchBlur}
        >
          <button
            className="gallery-search-trigger"
            type="button"
            aria-label={searchExpanded ? "聚焦搜索作品" : "展开搜索作品"}
            aria-expanded={searchExpanded}
            aria-controls="gallery-search-input"
            onClick={openSearch}
          >
            <Search size={16} aria-hidden="true" />
          </button>
          <label className="sr-only" htmlFor="gallery-search-input">搜索作品</label>
          <input
            ref={searchInputRef}
            id="gallery-search-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setSearchOpen(true)}
            placeholder="搜索提示词或模型"
            autoComplete="off"
          />
          {query ? (
            <IconButton className="gallery-search-clear" aria-label="清空搜索" onClick={clearSearch}>
              <X size={14} aria-hidden="true" />
            </IconButton>
          ) : null}
        </form>
      </div>

      <p className="sr-only" aria-live="polite">
        {filteredItems.length > 0
          ? `当前显示第 ${pageStart} 到 ${pageEnd} 张，共 ${filteredItems.length} 张。`
          : "当前没有可显示作品。"}
      </p>

      {pageItems.length === 0 ? (
        <div className="empty-state gallery-empty">
          <div className="empty-icon">
            <ImageIcon size={30} aria-hidden="true" />
          </div>
          <h2>{items.length > 0 ? "没有匹配作品" : "暂无作品"}</h2>
          <p>{items.length > 0 ? "换个关键词再试。" : "生成完成后的图片会显示在这里。"}</p>
        </div>
      ) : (
        <ul className="gallery-grid" aria-label={`作品列表，第 ${currentPage} 页`}>
          {pageItems.map((item, index) => {
            const itemLabel = item.prompt || item.model || "生成作品";
            const isFirstRow = index < 6;
            return (
              <li className="gallery-grid-item" key={item.id}>
                <MediaTile
                  className="gallery-tile"
                  onClick={() => onOpen(item.id)}
                  title={itemLabel}
                  aria-label={`查看作品：${itemLabel}`}
                >
                  <CachedImage
                    src={getThumbnailImageSrc(item)}
                    alt=""
                    loading={isFirstRow ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={isFirstRow ? "high" : "low"}
                    width={480}
                    height={360}
                    sizes="(max-width: 520px) calc((100vw - 96px) / 2), (max-width: 820px) calc((100vw - 120px) / 3), 16vw"
                    cache={false}
                  />
                  <span className="sr-only">{formatTimestamp(item.createdAt)}</span>
                </MediaTile>
              </li>
            );
          })}
        </ul>
      )}

      {filteredItems.length > pageSize ? (
        <nav className="gallery-pagination" aria-label="作品分页">
          <PaginationButton
            className="gallery-page-button"
            disabled={currentPage === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label="上一页"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </PaginationButton>
          {pageControls.map((pageNumber) => (
            typeof pageNumber === "number" ? (
              <PaginationButton
                className={pageNumber === currentPage ? "gallery-page-button active" : "gallery-page-button"}
                active={pageNumber === currentPage}
                key={pageNumber}
                aria-current={pageNumber === currentPage ? "page" : undefined}
                aria-label={`第 ${pageNumber} 页`}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </PaginationButton>
            ) : (
              <span className="gallery-page-ellipsis" key={pageNumber} aria-hidden="true">...</span>
            )
          ))}
          <PaginationButton
            className="gallery-page-button"
            disabled={currentPage === pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label="下一页"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </PaginationButton>
        </nav>
      ) : null}
    </section>
  );
}

function buildGalleryPagination(currentPage, pageCount) {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }
  const pages = new Set([1, pageCount, currentPage]);
  if (currentPage > 1) {
    pages.add(currentPage - 1);
  }
  if (currentPage < pageCount) {
    pages.add(currentPage + 1);
  }
  if (currentPage <= 4) {
    [2, 3, 4, 5].forEach((item) => pages.add(item));
  }
  if (currentPage >= pageCount - 3) {
    [pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1].forEach((item) => pages.add(item));
  }
  const sorted = Array.from(pages)
    .filter((item) => item >= 1 && item <= pageCount)
    .sort((left, right) => left - right);
  return sorted.flatMap((item, index) => (
    index > 0 && item - sorted[index - 1] > 1
      ? [`ellipsis-${item}`, item]
      : [item]
  ));
}
